import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { Columns, Row } from '@app/components/simple-table/simple-table.component';
import { JobPosting } from '@app/models/recruiting/job-posting.model';
import { Question } from '@app/models/recruiting/question.model';
import { NotifyService } from '@app/services';
import { mapQuestionToRow } from '../../functions/map-to-question-to-row.function';
import { additionalQuestionsTableColumns } from '../../meta/additional-question-table.columns';

@Component({
    selector: 'app-additional-questions-card',
    templateUrl: './additional-questions-card.component.html',
    styleUrls: ['./additional-questions-card.component.scss'],
})
export class AdditionalQuestionsCardComponent implements OnInit, OnChanges {
    @Input() questions: Question[] = [];
    @Input() jobPosting: JobPosting;
    @Input() isLoading = false;
    @Input() saveUsingHttp = false;

    // Will be used to trigger the parent component to pull updated data after an action is done.
    @Output() refreshData = new EventEmitter();

    dialogMode: 'edit' | 'add' = 'add';
    columns: Columns[] = additionalQuestionsTableColumns;
    question = new Question({ isAutoArchive: false, isRequired: false });
    deleteQuestionIndex: number;

    rows: Row[] = [];

    isAdditionalQuestionDialogOpen = false;
    isPreviousAdditionalQuestionDialogOpen = false;
    isDeleteDialogOpen = false;

    constructor(private notify: NotifyService) {}

    ngOnInit(): void {
        this.rows = this.questions.map(mapQuestionToRow);
    }

    ngOnChanges(): void {
        this.rows = this.questions.map(mapQuestionToRow);
    }

    async onQuestionAdded(question: Question): Promise<void> {
        if (this.saveUsingHttp) {
            this.isLoading = true;

            question.jobId = this.jobPosting.id;
            try {
                await question.save();
            } catch (err) {
                this.notify.error(err);
            } finally {
                this.isLoading = false;
            }

            this.refreshData.emit();

            return;
        }

        this.questions.push(question);
        this.rows = this.questions.map(mapQuestionToRow);
    }

    async onQuestionEdited(question: Question): Promise<void> {
        if (question.id) {
            this.isLoading = true;

            try {
                await question.save();
            } catch (err) {
                this.notify.error(err);
            } finally {
                this.isLoading = false;
            }

            this.refreshData.emit();

            return;
        }

        this.rows = this.questions.map(mapQuestionToRow);
    }

    openPreviousAdditionalQuestionDialog(): void {
        this.isPreviousAdditionalQuestionDialogOpen = true;
    }

    openAdditionalQuestionDialog(): void {
        this.dialogMode = 'add';
        this.isAdditionalQuestionDialogOpen = true;
    }

    openDeleteDialog(index: number): void {
        this.isDeleteDialogOpen = true;
        this.deleteQuestionIndex = index;
    }

    closeAdditionalQuestionDialog(): void {
        this.isAdditionalQuestionDialogOpen = false;
    }

    closePreviousAdditionalQuestionDialog(): void {
        this.isPreviousAdditionalQuestionDialogOpen = false;
    }

    closeDeleteDialog(): void {
        this.isDeleteDialogOpen = false;
    }

    onEdit(index: number): void {
        this.question = this.questions[index];
        this.dialogMode = 'edit';
        this.isAdditionalQuestionDialogOpen = true;
    }

    async onDelete(): Promise<void> {
        if (this.questions[this.deleteQuestionIndex].id) {
            this.isLoading = true;

            try {
                await this.questions[this.deleteQuestionIndex].delete();
            } catch (err) {
                this.notify.error(err);
            } finally {
                this.isLoading = false;
            }

            this.refreshData.emit();
            this.closeDeleteDialog();
            return;
        }

        this.questions.splice(this.deleteQuestionIndex, 1);
        this.rows = this.questions.map(mapQuestionToRow);
        this.isLoading = false;
        this.closeDeleteDialog();
    }

    async onPreviouslyQuestionAdded(questions: Question[]): Promise<void> {
        if (this.saveUsingHttp) {
            this.isLoading = true;
            const questionsPromises = questions.map((question: Question) => {
                question.jobId = this.jobPosting.id;
                return question.save();
            });

            try {
                await Promise.all(questionsPromises);
            } catch (err) {
                this.notify.error(err);
            } finally {
                this.isLoading = false;
            }

            this.refreshData.emit();

            return;
        }

        this.questions.push(...questions);
        this.rows = this.questions.map(mapQuestionToRow);
        this.isLoading = false;
    }
}
